/**
 * 教师端 DAG 攻防树（Optical 重构）：
 * - 自定义 SVG/HTML 节点：固定最小宽度、完整换行显示（彻底解决旧版 190px 截断）；
 * - 自定义贝塞尔边：带流光 dash 动画与端点脉冲，不再使用默认箭头；
 * - 背景点阵 + 冷光网格，节点激活态以点光源高亮。
 */
import { Component, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import '@xyflow/react/dist/style.css';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  Handle,
  Position,
  ReactFlow,
  addEdge,
  getBezierPath,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import type { DagNodeKind, MotionPackage, Seat, Side } from '@zongheng/core-schema';
import { Badge, Button, cx, EmptyState } from '../ui/primitives.tsx';
import { CornerReticles } from '../system/Reticles.tsx';
import {
  DAG_EDGE_LABEL,
  DAG_KIND_LABEL,
  buildDagModel,
  describeDagNode,
  type DagNodeModel,
} from './dagModel.ts';

const SIDE_COLORS: Record<Side, { line: string; text: string; glow: string }> = {
  AFF: { line: '#7DD3FC', text: '#BAE6FD', glow: 'rgba(125,211,252,0.32)' },
  NEG: { line: '#FDA4AF', text: '#FECDD3', glow: 'rgba(253,164,175,0.3)' },
};

interface TacticalNodeData extends Record<string, unknown> {
  label: string;
  summary: string;
  side: Side;
  kind: DagNodeKind;
  active: boolean;
  stageRange: [number, number];
  seat?: Seat;
  custom?: boolean;
  editing?: boolean;
}

class FlowErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(): void {
    /* jsdom / 老浏览器无法测量画布尺寸时静默降级 */
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function StaticDagFallback({ model }: { model: ReturnType<typeof buildDagModel> }) {
  return (
    <div className="h-full overflow-auto p-3" data-testid="dag-fallback">
      <p className="micro-label mb-2">画布尺寸不可用，已降级为静态节点列表</p>
      <ul className="space-y-1">
        {model.nodes.map((node) => (
          <li
            key={node.id}
            className={cx(
              'rounded-lg px-2 py-1.5 text-xs shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]',
              node.active ? 'bg-white/[0.06] text-slate-100' : 'bg-white/[0.02] text-slate-400',
            )}
          >
            <span className="mono mr-2 text-[9px] text-slate-500">
              {DAG_KIND_LABEL[node.kind]}
            </span>
            {node.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TacticalNode({ data, id }: NodeProps<Node<TacticalNodeData>>) {
  const palette = SIDE_COLORS[data.side];
  const connect = (kind: 'source' | 'target') => (event: MouseEvent) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('zg-dag-connect', { detail: { nodeId: id, kind } }));
  };
  return (
    <div
      className="relative rounded-[10px] px-3 py-2.5 transition-all"
      style={{
        minWidth: 210,
        maxWidth: 260,
        background: data.active
          ? 'linear-gradient(135deg, rgba(26,30,40,0.92), rgba(12,14,20,0.92))'
          : 'linear-gradient(135deg, rgba(16,18,24,0.86), rgba(9,10,14,0.86))',
        boxShadow: data.active
          ? `inset 0 1px 0 0 rgba(255,255,255,0.24), inset 0 0 0 1px ${palette.line}66, 0 18px 40px -22px rgba(0,0,0,0.95), 0 0 28px -14px ${palette.glow}`
          : 'inset 0 1px 0 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.04)',
        opacity: data.active ? 1 : 0.55,
      }}
    >
      {data.active && <CornerReticles inset={5} color={`${palette.line}99`} />}
      {data.editing && (
        <>
          <Handle
            type="target"
            position={Position.Left}
            onClick={connect('target')}
            style={{ width: 9, height: 9, background: '#E2E8F0', border: '1px solid #040507', zIndex: 5 }}
          />
          <Handle
            type="source"
            position={Position.Right}
            onClick={connect('source')}
            style={{ width: 9, height: 9, background: palette.line, border: '1px solid #040507', zIndex: 5 }}
          />
        </>
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="mono text-[8.5px] uppercase tracking-[0.12em] text-slate-500">
          {DAG_KIND_LABEL[data.kind]} · S{String(data.stageRange[0]).padStart(2, '0')}–
          {String(data.stageRange[1]).padStart(2, '0')}
          {data.seat ? ` · ${data.seat}辩发牌` : ''}
        </span>
        <span
          className="h-1 w-1 shrink-0 rounded-full"
          style={{
            background: palette.line,
            boxShadow: data.active ? `0 0 8px ${palette.line}` : 'none',
          }}
        />
      </div>
      <div
        className="mt-1 whitespace-normal text-[11.5px] font-semibold leading-snug"
        style={{ color: data.active ? '#F8FAFC' : '#CBD5E1', wordBreak: 'break-word' }}
      >
        {data.label}
      </div>
      <div className="mt-1 line-clamp-2 text-[9.5px] leading-relaxed text-slate-500">
        {data.summary}
      </div>
    </div>
  );
}

function TacticalEdge(props: EdgeProps) {
  const [path, labelX, labelY] = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
    curvature: 0.35,
  });
  const data = props.data as { color?: string; active?: boolean } | undefined;
  const color = data?.color ?? 'rgba(255,255,255,0.35)';
  return (
    <>
      <BaseEdge
        id={props.id}
        path={path}
        style={{
          stroke: color,
          strokeWidth: data?.active ? 1.6 : 1,
          opacity: data?.active ? 0.9 : 0.35,
        }}
      />
      {data?.active && (
        <>
          <path
            d={path}
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeDasharray="2 22"
            opacity="0.9"
            style={{ animation: 'dag-flow 1.6s linear infinite' }}
          />
          <circle cx={labelX} cy={labelY} r="2.2" fill="#FFFFFF" opacity="0.9">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.8s" repeatCount="indefinite" />
          </circle>
        </>
      )}
    </>
  );
}

const nodeTypes = { tactical: TacticalNode };
const edgeTypes = { tactical: TacticalEdge };

export function DagBoard({
  motion,
  currentStageIndex,
}: {
  motion: MotionPackage;
  currentStageIndex: number;
}) {
  const model = useMemo(
    () => buildDagModel(motion.dag, currentStageIndex),
    [motion.dag, currentStageIndex],
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNode = model.nodes.find((node) => node.id === selectedNodeId) ?? null;
  const nodeById = useMemo(() => new Map(model.nodes.map((node) => [node.id, node])), [model.nodes]);

  const nodes = useMemo<Node<TacticalNodeData>[]>(
    () =>
      model.nodes.map((node: DagNodeModel) => ({
        id: node.id,
        type: 'tactical',
        position: node.position,
        data: {
          label: node.label,
          summary: node.summary,
          side: node.side,
          kind: node.kind,
          active: node.active,
          stageRange: node.stageRange,
          editing: false,
        },
        draggable: false,
        selectable: true,
        selected: node.id === selectedNodeId,
      })),
    [model.nodes, selectedNodeId],
  );

  const edges = useMemo<Edge[]>(
    () =>
      model.edges.map((edge) => {
        const sourceSide = nodeById.get(edge.source)?.side ?? 'AFF';
        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: 'tactical',
          animated: false,
          data: {
            color: edge.active ? SIDE_COLORS[sourceSide].line : 'rgba(255,255,255,0.34)',
            active: edge.active,
            label: edge.label ?? DAG_EDGE_LABEL[edge.kind],
          },
        } satisfies Edge;
      }),
    [model.edges, nodeById],
  );

  const [editMode, setEditMode] = useState(false);
  const [editNodes, setEditNodes, onEditNodesChange] = useNodesState<Node<TacticalNodeData>>([]);
  const [editEdges, setEditEdges, onEditEdgesChange] = useEdgesState<Edge>([]);
  const [newSide, setNewSide] = useState<Side>('AFF');
  const [newSeat, setNewSeat] = useState(1);
  const [newStage, setNewStage] = useState(Math.max(1, currentStageIndex));
  const [flowNote, setFlowNote] = useState('');
  const [pendingSource, setPendingSource] = useState<string | null>(null);
  const planKey = `zongheng.dag.plan.${motion.motionId}`;

  const beginEdit = () => {    let saved: { nodes?: Node<TacticalNodeData>[]; edges?: Edge[] } | null = null;
    try {
      const raw = localStorage.getItem(planKey);
      saved = raw ? (JSON.parse(raw) as { nodes?: Node<TacticalNodeData>[]; edges?: Edge[] }) : null;
    } catch {
      saved = null;
    }
    setEditNodes(
      saved?.nodes?.length
        ? saved.nodes.map((node) => ({ ...node, draggable: true, data: { ...node.data, editing: true, active: true } }))
        : nodes.map((node) => ({ ...node, draggable: true, data: { ...node.data, editing: true, active: true } })),
    );
    setEditEdges(
      saved?.edges?.length
        ? saved.edges
        : edges.map((edge) => ({ ...edge, data: { ...edge.data, active: true } })),
    );
    setEditMode(true);
    setFlowNote(saved?.nodes?.length ? '已载入本机保存的流程草案' : '编辑中：拖动节点、拖拽连线、按 Delete 删除');
  };

  const saveEdit = () => {
    try {
      localStorage.setItem(planKey, JSON.stringify({ nodes: editNodes, edges: editEdges }));
      setFlowNote('流程已保存到本机；下一场开始后按“触发环节 + 辩位”投递底牌（Phase 1.5 同步到房间）');
    } catch {
      setFlowNote('本机存储不可用，草案仅保留在当前页面');
    }
    setEditMode(false);
  };

  const addFlowNode = () => {
    const seatLabel = ['一', '二', '三', '四'][newSeat - 1];
    const node: Node<TacticalNodeData> = {
      id: `custom-${Date.now()}`,
      type: 'tactical',
      position: { x: 80 + Math.random() * 420, y: 80 + Math.random() * 300 },
      data: {
        label: `${newSide === 'AFF' ? '正方' : '反方'}${seatLabel}辩 · 自定义节点`,
        summary: `教师自定义流程节点：辩位 ${seatLabel}，触发环节 ${newStage}。`,
        side: newSide,
        kind: 'EVIDENCE',
        active: true,
        stageRange: [newStage, newStage],
        seat: newSeat as Seat,
        custom: true,
        editing: true,
      },
      draggable: true,
    };
    setEditNodes((current) => [...current, node]);
  };

  const randomizeFlow = () => {
    setEditNodes((current) =>
      current.map((node, index) => ({
        ...node,
        position: {
          x: 40 + ((index * 230) % 900) + Math.random() * 80,
          y: 50 + ((Math.floor(index / 4) * 160) % 420) + Math.random() * 60,
        },
      })),
    );
  };

  const randomizeSeats = () => {
    setEditNodes((current) => {
      const seats = [1, 2, 3, 4] as Seat[];
      for (let index = seats.length - 1; index > 0; index -= 1) {
        const swap = Math.floor(Math.random() * (index + 1));
        [seats[index], seats[swap]] = [seats[swap] as Seat, seats[index] as Seat];
      }
      let cursor = 0;
      return current.map((node) =>
        node.data.custom
          ? { ...node, data: { ...node.data, seat: seats[cursor++ % seats.length] } }
          : node,
      );
    });
    setFlowNote('已随机重排自定义节点的发牌辩位；保存后下一场生效（按节点触发环节匹配）。');
  };

  const randomizeTriggers = () => {
    setEditNodes((current) =>
      current.map((node) => {
        if (!node.data.custom) return node;
        const stageIndex = 1 + Math.floor(Math.random() * 16);
        return {
          ...node,
          data: { ...node.data, stageRange: [stageIndex, stageIndex] },
        };
      }),
    );
    setFlowNote('已随机分配自定义节点的触发环节；保存后下一场按新顺序发牌。');
  };

  useEffect(() => {
    if (!editMode) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ nodeId: string; kind: 'source' | 'target' }>).detail;
      if (!detail?.nodeId) return;
      if (detail.kind === 'source') {
        setPendingSource(detail.nodeId);
        setFlowNote('已选起点：现在点击另一个节点的左侧圆点完成连线（也可直接拖拽圆点）。');
        return;
      }
      if (pendingSource && pendingSource !== detail.nodeId) {
        setEditEdges((current) =>
          addEdge(
            {
              source: pendingSource,
              target: detail.nodeId,
              sourceHandle: null,
              targetHandle: null,
              type: 'tactical',
              data: { color: 'rgba(255,255,255,0.7)', active: true, label: '自定义触发' },
            },
            current,
          ),
        );
        setPendingSource(null);
        setFlowNote('连线已添加；保存后进入本场流程。');
      }
    };
    window.addEventListener('zg-dag-connect', handler);
    return () => window.removeEventListener('zg-dag-connect', handler);
  }, [editMode, pendingSource, setEditEdges]);

  const detail = selectedNode
    ? describeDagNode(selectedNode, motion.evidenceLibrary)
    : null;

  return (
    <section className="lens spotlight-card relative overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-2 px-5 pb-2 pt-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="status-dot status-dot--live" />
            <h2 className="text-[13px] font-medium tracking-wide text-slate-100">
              DAG 攻防树 · 实时路径
            </h2>
          </div>
          <p className="micro-label mt-1">
            STAGE {String(currentStageIndex || 0).padStart(2, '0')} ACTIVE PATH · 点击节点查看摘要
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {editMode ? (
            <>
              <select
                value={newSide}
                onChange={(event) => setNewSide(event.target.value as Side)}
                aria-label="新节点阵营"
                className="rounded border border-white/10 bg-black/30 px-1.5 py-1 text-[10px] text-slate-200"
              >
                <option value="AFF">正方</option>
                <option value="NEG">反方</option>
              </select>
              <select
                value={newSeat}
                onChange={(event) => setNewSeat(Number(event.target.value))}
                aria-label="新节点辩位"
                className="rounded border border-white/10 bg-black/30 px-1.5 py-1 text-[10px] text-slate-200"
              >
                {[1, 2, 3, 4].map((seat) => (
                  <option key={seat} value={seat}>
                    {seat} 辩
                  </option>
                ))}
              </select>
              <select
                value={newStage}
                onChange={(event) => setNewStage(Number(event.target.value))}
                aria-label="新节点触发环节"
                className="rounded border border-white/10 bg-black/30 px-1.5 py-1 text-[10px] text-slate-200"
              >
                {Array.from({ length: 16 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    S{String(index + 1).padStart(2, '0')}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="secondary" onClick={addFlowNode} testId="dag-add-node">
                + 节点
              </Button>
              <Button size="sm" variant="ghost" onClick={randomizeFlow} testId="dag-randomize">
                随机布局
              </Button>
              <Button size="sm" variant="ghost" onClick={randomizeSeats} testId="dag-randomize-seats">
                随机辩位
              </Button>
              <Button size="sm" variant="ghost" onClick={randomizeTriggers} testId="dag-randomize-triggers">
                随机触发
              </Button>
              <Button size="sm" variant="primary" onClick={saveEdit} testId="dag-save">
                保存流程
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditMode(false)} testId="dag-cancel">
                退出编辑
              </Button>
            </>
          ) : (
            <Button size="sm" variant="secondary" onClick={beginEdit} testId="dag-edit">
              编辑赛前流程
            </Button>
          )}
          <Badge tone="aff">正方 AFF</Badge>
          <Badge tone="neg">反方 NEG</Badge>
          <span className="mono text-[10px] text-slate-500">
            {model.activeNodeIds.length} ACTIVE
          </span>
        </div>
      </header>
      {editMode && (
        <div className="mx-5 mb-1 rounded-md border border-[#FCD34D]/25 bg-[#FCD34D]/[0.05] px-2.5 py-1.5 text-[10px] text-[#FDE68A]">
          {flowNote || '拖动节点调整位置；从节点边缘拖到另一节点创建连线；可随机布局。'}
          <span className="ml-2 text-slate-500">保存后可在本机复用；Phase 1.5 写入房间 Yjs。</span>
        </div>
      )}
      <div className="hairline mx-5" />
      <div className="grid gap-2 p-3 lg:grid-cols-[minmax(0,1fr)_248px]">
        <div
          data-testid="dag-canvas"
          style={{ height: 470 }}
          className="relative overflow-hidden rounded-[12px] bg-white/[0.012] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),inset_0_0_0_1px_rgba(255,255,255,0.025)]"
        >
          <CornerReticles inset={8} color="rgba(255,255,255,0.22)" />
          <FlowErrorBoundary fallback={<StaticDagFallback model={model} />}>
            <ReactFlow
              nodes={editMode ? editNodes : nodes}
              edges={editMode ? editEdges : edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodeClick={(_event, node) => setSelectedNodeId(node.id)}
              onNodesChange={editMode ? onEditNodesChange : undefined}
              onEdgesChange={editMode ? onEditEdgesChange : undefined}
              onConnect={(connection: Connection) =>
                setEditEdges((current) =>
                  addEdge(
                    {
                      ...connection,
                      type: 'tactical',
                      data: { color: 'rgba(255,255,255,0.7)', active: true, label: '自定义触发' },
                    },
                    current,
                  ),
                )
              }
              nodesDraggable={editMode}
              nodesConnectable={editMode}
              deleteKeyCode={editMode ? ['Backspace', 'Delete'] : null}
              elementsSelectable
              minZoom={0.2}
              maxZoom={1.6}
              defaultViewport={{ x: 0, y: 0, zoom: 0.72 }}
              proOptions={{ hideAttribution: true }}
            >
              <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="rgba(255,255,255,0.09)" />
              <Controls showInteractive={false} />
            </ReactFlow>
          </FlowErrorBoundary>
        </div>
        <aside className="lens-inset flex flex-col gap-3 p-3" data-testid="dag-node-detail">
          {detail ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={detail.node.side === 'AFF' ? 'aff' : 'neg'}>
                  {detail.node.side === 'AFF' ? '正方' : '反方'} · {DAG_KIND_LABEL[detail.node.kind]}
                </Badge>
                <span className="mono text-[10px] text-slate-500">
                  STAGE {detail.node.stageRange[0]}–{detail.node.stageRange[1]}
                </span>
              </div>
              <div className="text-[14px] font-semibold leading-snug text-slate-100">
                {detail.node.label}
              </div>
              <p className="text-[11px] leading-relaxed text-slate-400">{detail.node.summary}</p>
              <div className="hairline" />
              <div className="micro-label">引用论据</div>
              {detail.evidence.length === 0 ? (
                <p className="text-[11px] text-slate-500">该节点未绑定论据。</p>
              ) : (
                <ul className="space-y-2">
                  {detail.evidence.map((evidence) => (
                    <li key={evidence.id} data-testid="dag-evidence">
                      <div className="text-[11px] font-medium text-slate-200">{evidence.shortTitle}</div>
                      <div className="mt-0.5 text-[10px] leading-relaxed text-slate-500">
                        {evidence.exactQuoteOrStats}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <EmptyState title="未选择节点" detail="点击画布中的节点查看摘要与引用论据。" />
          )}
        </aside>
      </div>
      <style>{`@keyframes dag-flow { to { stroke-dashoffset: -48; } }`}</style>
    </section>
  );
}

export type { DagNodeModel };
